import { SettingsBackLink } from "@/components/settings/SettingsBackLink";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, GripVertical, Plus, Trash2, Settings as SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { listModules, getModuleDef, MODULE_REGISTRY } from "@/workflows/moduleRegistry";
import type { WorkflowModuleRow, WorkflowDefinitionRow } from "@/workflows/types";
import { z } from "zod";

type Draft = WorkflowModuleRow & { _dirty?: boolean; _new?: boolean };

export default function WorkflowDesigner() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isSuperadmin: isAdmin, isLoading: roleLoading } = useIsSuperadmin();

  const { data, isLoading } = useQuery({
    queryKey: ["workflow_designer", key],
    enabled: !!key,
    queryFn: async () => {
      const { data: def } = await (supabase as any)
        .from("workflow_definitions").select("*").eq("key", key).maybeSingle();
      if (!def) return null;
      const { data: mods } = await (supabase as any)
        .from("workflow_modules").select("*")
        .eq("workflow_definition_id", def.id).order("position");
      return { definition: def as WorkflowDefinitionRow, modules: (mods ?? []) as WorkflowModuleRow[] };
    },
  });

  const [items, setItems] = useState<Draft[]>([]);
  const [configDraft, setConfigDraft] = useState<Draft | null>(null);

  useEffect(() => { if (data?.modules) setItems(data.modules); }, [data?.modules]);

  if (roleLoading) return null;
  // Access enforced by the route guard in App.tsx.
  if (isLoading) return <div className="p-6">Loading…</div>;
  if (!data) return <div className="p-6">Workflow not found.</div>;

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next.map((m, i) => ({ ...m, position: (i + 1) * 10, _dirty: true })));
  };

  const addModule = (moduleKey: string) => {
    const def = getModuleDef(moduleKey)!;
    const tempId = `tmp-${crypto.randomUUID()}`;
    setItems((prev) => [
      ...prev,
      {
        id: tempId,
        workflow_definition_id: data.definition.id,
        module_key: moduleKey,
        position: (prev.length + 1) * 10,
        title_override: null,
        config: { ...(def.defaultConfig as object) },
        is_required: true,
        visible_when: {},
        _new: true,
        _dirty: true,
      } as Draft,
    ]);
  };

  const removeModule = (idx: number) => {
    setItems(items.filter((_, i) => i !== idx).map((m, i) => ({ ...m, position: (i + 1) * 10, _dirty: true })));
  };

  const save = async () => {
    try {
      // Strategy: delete all existing, insert current items
      const existingIds = (data.modules || []).map((m) => m.id);
      if (existingIds.length) {
        const { error: delErr } = await (supabase as any)
          .from("workflow_modules").delete().in("id", existingIds);
        if (delErr) throw delErr;
      }
      const payload = items.map((m, i) => ({
        workflow_definition_id: data.definition.id,
        module_key: m.module_key,
        position: (i + 1) * 10,
        title_override: m.title_override,
        config: m.config ?? {},
        is_required: m.is_required ?? true,
        visible_when: m.visible_when ?? {},
      }));
      if (payload.length) {
        const { error } = await (supabase as any).from("workflow_modules").insert(payload);
        if (error) throw error;
      }
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["workflow_designer", key] });
      qc.invalidateQueries({ queryKey: ["workflow_definition", key] });
    } catch (err: any) {
      console.error(err);
      toast.error(err.message ?? "Save failed");
    }
  };

  const available = listModules(key);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-6 pb-3">
        <SettingsBackLink to="/platform/workflows" label="Workflows" />
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{data.definition.name} — Designer</h1>
            <p className="text-sm text-muted-foreground">Compose modules. Drag to reorder, configure, save.</p>
          </div>
          <Button onClick={save}>Save</Button>
        </div>
      </div>


      <div className="px-6 space-y-3">
        {items.map((m, idx) => {
          const def = getModuleDef(m.module_key);
          return (
            <Card key={m.id} className="p-4 flex items-center gap-3">
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} className="text-xs hover:bg-muted px-1">▲</button>
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                <button onClick={() => move(idx, 1)} className="text-xs hover:bg-muted px-1">▼</button>
              </div>
              <div className="flex-1">
                <div className="font-medium">{m.title_override || def?.label || m.module_key}</div>
                <div className="text-xs text-muted-foreground">{def?.description || m.module_key}</div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Required</Label>
                <Switch
                  checked={m.is_required}
                  onCheckedChange={(v) => {
                    const next = [...items];
                    next[idx] = { ...next[idx], is_required: v, _dirty: true };
                    setItems(next);
                  }}
                />
                <Button variant="outline" size="sm" onClick={() => setConfigDraft(m)}>
                  <SettingsIcon className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeModule(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          );
        })}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Add module
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-72">
            {available.map((d) => (
              <DropdownMenuItem key={d.key} onClick={() => addModule(d.key)}>
                <div>
                  <div className="font-medium">{d.label}</div>
                  <div className="text-xs text-muted-foreground">{d.description}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfigDialog
        draft={configDraft}
        onClose={() => setConfigDraft(null)}
        onSave={(updated) => {
          setItems((prev) => prev.map((m) => (m.id === updated.id ? { ...updated, _dirty: true } : m)));
          setConfigDraft(null);
        }}
      />
    </div>
  );
}

function ConfigDialog({ draft, onClose, onSave }: { draft: Draft | null; onClose: () => void; onSave: (d: Draft) => void }) {
  const [title, setTitle] = useState("");
  const [json, setJson] = useState("{}");

  useEffect(() => {
    if (draft) {
      setTitle(draft.title_override ?? "");
      setJson(JSON.stringify(draft.config ?? {}, null, 2));
    }
  }, [draft]);

  if (!draft) return null;
  const def = getModuleDef(draft.module_key);

  return (
    <Dialog open={!!draft} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configure {def?.label || draft.module_key}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title override</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={def?.label} />
          </div>
          <div>
            <Label>Config (JSON)</Label>
            <textarea
              className="w-full font-mono text-xs border rounded-md p-2 min-h-[160px]"
              value={json}
              onChange={(e) => setJson(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Validated against the module schema on save.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              try {
                const parsed = JSON.parse(json || "{}");
                const validated = def?.configSchema
                  ? (def.configSchema as z.ZodTypeAny).parse(parsed)
                  : parsed;
                onSave({ ...draft, title_override: title || null, config: validated });
              } catch (e: any) {
                toast.error(e.message ?? "Invalid config");
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
